import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSaveSiteConfigurationMutation } from "../../../../../services/supplyChainService";
import sustainabilityTemplate from "./template.json";
import { useImageProcessing } from "../../../../../utils/imageUtils";
import SustainabilityConfiguration from "./SustainabilityConfiguration";

export default function SustainabilityPageController({
    projectId,
    allConfigurations,
    isLoading: isLoadingAll,
    configId,
    setConfigId,
    initialValues,
    setInitialValues,
    pageConfigurations,
    setPageConfigurations,
}) {
    // API hooks
    const [saveSiteConfiguration, { isLoading: isSaving }] =
        useSaveSiteConfigurationMutation();
    const { processImage } = useImageProcessing();

    // Image state management
    const [sustainabilityBannerImage, setSustainabilityBannerImage] =
        useState(null);
    const [standardImages, setstandardImages] = useState([]);
    const [profitSectionTopImage, setProfitSectionTopImage] = useState(null);
    const [profitSectionBottomImage, setProfitSectionBottomImage] =
        useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize configuration from existing data
    useEffect(() => {
        if (!allConfigurations?.data) return;

        const sustainabilityConfig = allConfigurations?.data?.find?.(
            (config) => config.config_type === "SUSTAINABILITY",
        );

        if (sustainabilityConfig) {
            setConfigId(sustainabilityConfig.id);
            const sustainabilityConfigData =
                sustainabilityConfig.config_data || {};

            // Set images from existing configuration
            if (sustainabilityConfigData.banner_image_file_url) {
                setSustainabilityBannerImage(
                    sustainabilityConfigData.banner_image_file_url,
                );
            }
            if (sustainabilityConfigData.standards?.length) {
                setstandardImages(
                    sustainabilityConfigData?.standards?.map?.(
                        (standard) => standard.standard_image_file_url || null,
                    ),
                );
            }

            if (sustainabilityConfigData.profit_section_image_top_file_url) {
                setProfitSectionTopImage(
                    sustainabilityConfigData.profit_section_image_top_file_url,
                );
            }

            if (sustainabilityConfigData.profit_section_image_bottom_file_url) {
                setProfitSectionBottomImage(
                    sustainabilityConfigData.profit_section_image_bottom_file_url,
                );
            }

            setInitialValues({
                ...sustainabilityConfigData,
            });
        }
    }, [allConfigurations, setConfigId, setInitialValues]);

    // Image selection handlers
    const handleSustainabilityBannerSelected = (file) => {
        if (file !== undefined) {
            setSustainabilityBannerImage(file);
        }
    };

    const handleStandardImageSelected = (file, index) => {
        setstandardImages((prev) => {
            const newImages = [...prev];
            newImages[index] = file;
            return newImages;
        });
    };

    const handleProfitSectionTopImageSelected = (file) => {
        if (file !== undefined) {
            setProfitSectionTopImage(file);
        }
    };

    const handleProfitSectionBottomImageSelected = (file) => {
        if (file !== undefined) {
            setProfitSectionBottomImage(file);
        }
    };

    // Form submission handler
    const handleSubmit = async (values) => {
        setIsSubmitting(true); 
        try {
            // Process all images in parallel
            const bannerImageFileId = await processImage(
                sustainabilityBannerImage,
                initialValues.banner_image_file_id,
            );

            // Process profit section images
            const profitSectionTopImageFileId = await processImage(
                profitSectionTopImage,
                initialValues.profit_section_image_top_file_id,
            );

            const profitSectionBottomImageFileId = await processImage(
                profitSectionBottomImage,
                initialValues.profit_section_image_bottom_file_id,
            );

            // Determine page enablement status
            const existingConfiguration =
                pageConfigurations.sustainability?.exists;

            const isEnabled = existingConfiguration
                ? pageConfigurations.sustainability?.enabled !== false
                : true;

            // Process projects
            const processProjects = await Promise.all(
                values.sustainability_projects
                    .filter(
                        (project) =>
                            project.project_title ||
                            project.project_description,
                    ) // Filter out empty projects
                    .map(async (project) => {
                        return {
                            project_title: project.project_title || null,
                            project_description:
                                project.project_description || null,
                        };
                    }),
            );

            const configData = {
                ...sustainabilityTemplate,
                config_data: {
                    page_title: values.page_title ?? "",
                    page_description: values.page_description ?? "",
                    banner_image_file_id: bannerImageFileId,
                    sustainability_title: values.sustainability_title ?? "",
                    sustainability_description:
                        values.sustainability_description ?? "",
                    external_button: {
                        button_text: values.external_button?.button_text ?? "",
                        button_link: values.external_button?.button_link ?? "",
                    },
                    sustainability_projects: processProjects,
                    profit_section_title: values.profit_section_title ?? "",
                    profit_section_highlighted_percentage:
                        values.profit_section_highlighted_percentage ?? "",
                    profit_section_highlighted_text:
                        values.profit_section_highlighted_text ?? "",
                    profit_section_image_top_file_id:
                        profitSectionTopImageFileId,
                    profit_section_image_bottom_file_id:
                        profitSectionBottomImageFileId,
                },
                enabled: isEnabled,
                required: sustainabilityTemplate.required,
            };

            // Save configuration to backend
            const result = await saveSiteConfiguration({
                id: projectId,
                configData,
            }).unwrap();

            // Update local state with new configuration
            if (result?.data?.id) {
                setConfigId(result.data.id);
                setPageConfigurations((prev) => ({
                    ...prev,
                    sustainability: {
                        ...prev.sustainability,
                        id: result.data.id,
                        exists: true,
                        enabled: existingConfiguration
                            ? prev.sustainability.enabled
                            : true,
                    },
                }));
            }

            toast.success(
                "Sustainability page configuration saved successfully",
            );
        } catch (error) {
            console.error("Error saving sustainability configuration:", error);
            toast.error("Failed to save sustainability page configuration");
        } finally {
            setIsSubmitting(false); 
        }
    };

    return (
        <SustainabilityConfiguration
            handleSubmit={handleSubmit}
            initialValues={initialValues}
            isLoading={isSaving || isLoadingAll || isSubmitting} // Include new loading state
            sustainabilityBannerImage={sustainabilityBannerImage}
            handleSustainabilityBannerSelected={
                handleSustainabilityBannerSelected
            }
            standardImages={standardImages}
            handleStandardImageselected={handleStandardImageSelected}
            profitSectionTopImage={profitSectionTopImage}
            profitSectionBottomImage={profitSectionBottomImage}
            handleProfitSectionTopImageSelected={
                handleProfitSectionTopImageSelected
            }
            handleProfitSectionBottomImageSelected={
                handleProfitSectionBottomImageSelected
            }
        />
    );
}
